import React, { useEffect, useState } from "react";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Receipt, Users, Grid3x3, Clock, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

const KpiCard = ({ icon: Icon, label, value, sub, testId }) => (
    <Card className="grain bg-card/70" data-testid={testId}>
        <CardContent className="p-5">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
                    <div className="text-2xl md:text-3xl font-medium tabular-nums">{value}</div>
                    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
                </div>
                <div className="rounded-xl bg-primary/15 text-primary p-2.5">
                    <Icon className="h-5 w-5" />
                </div>
            </div>
        </CardContent>
    </Card>
);

export default function Dashboard() {
    const { t, lang, user } = useApp();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    const greet = () => {
        const h = new Date().getHours();
        if (h < 12) return t("good_morning");
        if (h < 17) return t("good_afternoon");
        return t("good_evening");
    };

    if (!stats) return <div className="text-muted-foreground">{t("loading")}</div>;

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
                <div>
                    <div className="text-sm text-muted-foreground">{greet()},</div>
                    <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl md:text-4xl`}>{user?.name}</h1>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard testId="kpi-today-revenue" icon={TrendingUp} label={t("revenue_today")} value={fmtSDG(stats.today.total, lang)} sub={`${stats.today.orders} ${t("orders_count")}`} />
                <KpiCard testId="kpi-month-revenue" icon={Receipt} label={t("revenue_month")} value={fmtSDG(stats.month.total, lang)} sub={`${stats.month.orders} ${t("orders_count")}`} />
                <KpiCard testId="kpi-tables" icon={Grid3x3} label={t("currently_active")} value={`${stats.tables.occupied}/${stats.tables.total}`} sub={`${stats.tables.free} ${t("free_tables")}`} />
                <KpiCard testId="kpi-pending" icon={Clock} label={t("pending_orders")} value={stats.pending_orders} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="grain bg-card/70" data-testid="chart-daily-sales">
                    <CardContent className="p-5">
                        <div className="text-sm font-medium mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />{t("sales_last_7")}</div>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={stats.daily_series}>
                                    <CartesianGrid stroke="#3d2616" strokeDasharray="3 3" />
                                    <XAxis dataKey="date" stroke="#c4a48c" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                                    <YAxis stroke="#c4a48c" fontSize={11} />
                                    <Tooltip contentStyle={{ background: "#1a0f08", border: "1px solid #3d2616", borderRadius: 12 }} labelStyle={{ color: "#f5ebd9" }} />
                                    <Line type="monotone" dataKey="total" stroke="#d4a35c" strokeWidth={2.5} dot={{ fill: "#d4a35c", r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                <Card className="grain bg-card/70" data-testid="chart-busy-hours">
                    <CardContent className="p-5">
                        <div className="text-sm font-medium mb-4 flex items-center gap-2"><Clock className="h-4 w-4 text-primary" />{t("busy_hours")}</div>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.busy_hours}>
                                    <CartesianGrid stroke="#3d2616" strokeDasharray="3 3" />
                                    <XAxis dataKey="hour" stroke="#c4a48c" fontSize={11} />
                                    <YAxis stroke="#c4a48c" fontSize={11} />
                                    <Tooltip contentStyle={{ background: "#1a0f08", border: "1px solid #3d2616", borderRadius: 12 }} labelStyle={{ color: "#f5ebd9" }} />
                                    <Bar dataKey="count" fill="#d4a35c" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="grain bg-card/70" data-testid="top-items-card">
                    <CardContent className="p-5">
                        <div className="text-sm font-medium mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-primary" />{t("top_items")}</div>
                        {stats.top_items.length === 0 ? (
                            <div className="text-sm text-muted-foreground">{t("no_items")}</div>
                        ) : (
                            <div className="space-y-3">
                                {stats.top_items.map((it, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-3">
                                            <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center font-medium">{i + 1}</span>
                                            <span>{lang === "ar" ? it.name_ar : it.name_en}</span>
                                        </div>
                                        <div className="text-muted-foreground tabular-nums">
                                            <span className="me-2">{it.qty} ×</span>
                                            <span className="text-primary font-medium">{fmtSDG(it.revenue, lang)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="grain bg-card/70" data-testid="staff-perf-card">
                    <CardContent className="p-5">
                        <div className="text-sm font-medium mb-4 flex items-center gap-2"><Users className="h-4 w-4 text-primary" />{t("staff_perf")}</div>
                        {stats.staff_performance.length === 0 ? (
                            <div className="text-sm text-muted-foreground">—</div>
                        ) : (
                            <div className="space-y-3">
                                {stats.staff_performance.map((s, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                        <span>{s.name}</span>
                                        <div className="text-muted-foreground tabular-nums">
                                            <span className="me-2">{s.orders} {t("orders_count")}</span>
                                            <span className="text-primary font-medium">{fmtSDG(s.revenue, lang)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card className="grain bg-card/70" data-testid="quick-actions-card">
                <CardContent className="p-5">
                    <div className="text-sm font-medium mb-4">{t("quick_actions")}</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Link to="/dashboard/menu" data-testid="qa-menu" className="rounded-xl border border-border p-4 text-center hover:border-primary/60 active:scale-95 transition-all">{t("manage_menu")}</Link>
                        <Link to="/dashboard/tables" data-testid="qa-tables" className="rounded-xl border border-border p-4 text-center hover:border-primary/60 active:scale-95 transition-all">{t("manage_tables")}</Link>
                        <Link to="/dashboard/staff" data-testid="qa-staff" className="rounded-xl border border-border p-4 text-center hover:border-primary/60 active:scale-95 transition-all">{t("manage_staff")}</Link>
                        <Link to="/dashboard/orders" data-testid="qa-orders" className="rounded-xl border border-border p-4 text-center hover:border-primary/60 active:scale-95 transition-all">{t("view_orders")}</Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
